// Detailed, example-driven "how it works" content for every product.
// Rendered by <ProductGuide product="…"/> on each manager page and in the
// Services "How to use" drawer. Written for a non-technical owner: what it is,
// how it actually works step by step, and one concrete worked example.

export const PRODUCT_GUIDES = {
  answers: {
    name: 'AI Support Chatbot',
    tagline: 'A support bot trained on your website that lives as a chat bubble for your visitors.',
    how: [
      { t: 'It learns your site', d: 'When you activate it, we crawl your website and turn every page into the bot\'s knowledge — products, pricing, policies, FAQs.' },
      { t: 'You test it privately', d: 'Use the Preview to ask it questions. Only you see this; your visitors don\'t yet.' },
      { t: 'You paste one snippet to go live', d: 'Copy the <script> tag into your site\'s HTML before </body> and redeploy. A chat bubble appears on every page.' },
      { t: 'It stays fresh & safe', d: 'It re-reads your site daily, escalates when unsure, and only answers on domains you approve.' },
    ],
    example: {
      scenario: 'You run an online shoe store and want visitors to get instant answers.',
      steps: [
        { do: 'Activate with your URL, e.g. https://mystore.com', then: 'The bot reads your product, shipping and returns pages.' },
        { do: 'In Preview, ask "Do you ship to Canada?"', then: 'It answers from your shipping page: "Yes — 5–7 business days to Canada."' },
        { do: 'Paste the snippet on your site and publish', then: 'Every visitor now sees the chat bubble and gets the same answers.' },
      ],
    },
  },

  sync: {
    name: 'Knowledge Sync',
    tagline: 'An always-fresh, searchable copy of your website/content that your own apps can query.',
    how: [
      { t: 'You give it a source', d: 'Point it at your site URL, or paste/push text. It fetches the content.' },
      { t: 'It indexes for meaning', d: 'It splits the content into passages and stores a "meaning fingerprint" (embedding) for each, so search matches by intent — not just keywords.' },
      { t: 'It refreshes daily', d: 'It re-crawls on a schedule, so when your content changes the index updates automatically — no stale answers.' },
      { t: 'Your app searches it', d: 'Call the search API (or the box on this page) with a question and get back the most relevant passages, ranked.' },
    ],
    example: {
      scenario: 'Your help-docs site changes weekly and you want reliable in-app search.',
      steps: [
        { do: 'Sync https://docs.mysite.com', then: 'Every help article is indexed into your private search.' },
        { do: 'Search "how do I reset my password"', then: 'It returns the exact "Reset password" section — even if the page never uses the word "reset".' },
        { do: 'You edit that article tomorrow', then: 'The daily refresh re-indexes it; search stays current with zero effort.' },
      ],
    },
  },

  rag: {
    name: 'Custom RAG',
    tagline: 'Turn your documents into a private Q&A brain that answers questions with citations.',
    how: [
      { t: 'You add content to a collection', d: 'Paste text, add a document, or point at a URL. A "collection" is your private, isolated knowledge store — no other customer can see it.' },
      { t: 'It chunks & embeds', d: 'Each document is split into small overlapping passages (~1,000 chars) and converted into vectors, so an answer can point to the exact passage it came from.' },
      { t: 'You ask a question', d: 'Your question is embedded and compared to every passage; the closest ones are retrieved (semantic search).' },
      { t: 'The AI answers from those passages only', d: 'It writes an answer grounded strictly in the retrieved passages — and returns the sources, so you can trust and verify it.' },
    ],
    example: {
      scenario: 'You want a bot that answers refund questions straight from your policy — no hallucinations.',
      steps: [
        { do: 'Create a collection "Store Policies"', then: 'An empty private collection is ready.' },
        { do: 'Add your policy text: "Refunds accepted within 30 days of delivery for unworn items…"', then: 'It\'s chunked and embedded into your collection.' },
        { do: 'Ask "How long do I have to return shoes?"', then: 'It answers "You have 30 days from delivery to return unworn items." and shows the exact source passage.' },
      ],
    },
  },

  orchestration: {
    name: 'Agentic Workflows',
    tagline: 'Chain several AI agents so each one\'s output feeds the next — like an assembly line for a task.',
    how: [
      { t: 'A workflow is a set of steps', d: 'Each step is an agent with a job (a prompt). A step can depend on earlier steps.' },
      { t: 'Steps run in the right order', d: 'When you run the workflow with an input, steps execute in dependency order — a step waits for the ones it depends on.' },
      { t: 'Output flows downstream', d: 'Each step\'s result is passed to the steps that depend on it, so later agents build on earlier work.' },
      { t: 'The last step is the result', d: 'The final step\'s output is what the workflow returns.' },
    ],
    example: {
      scenario: 'Turn an angry incoming support message into a triaged, drafted reply.',
      steps: [
        { do: 'Step 1 "classify": read the message, decide urgency + category', then: 'For "My order #123 never arrived and I\'m furious" → {urgency: high, category: shipping}.' },
        { do: 'Step 2 "draft" (depends on classify): write a fitting reply', then: 'Uses the classification → "I\'m so sorry about order #123. I\'ve escalated your delayed shipment…"' },
        { do: 'Run the workflow with the message', then: 'You get the final drafted reply, produced by both agents in sequence.' },
      ],
    },
  },

  middleware: {
    name: 'Integration Adapters',
    tagline: 'Catch data from any inbound source (a form, a legacy system, a webhook) and reshape it into your pipeline.',
    how: [
      { t: 'You define an adapter', d: 'Pick a source (a webhook/API), transform rules (rename/map/enrich fields), and a target (your AI pipeline, a knowledge base, etc.).' },
      { t: 'You point a system at its URL', d: 'The adapter gives you a webhook URL. Configure your form or legacy tool to POST to it.' },
      { t: 'It reshapes on arrival', d: 'When data lands, the adapter applies your rules — renaming and restructuring fields into the exact shape your pipeline expects.' },
      { t: 'It forwards the clean payload', d: 'The normalized data flows on to your target automatically.' },
    ],
    example: {
      scenario: 'Your website contact form should feed your AI, but its field names don\'t match.',
      steps: [
        { do: 'Your form sends { message, email }; your pipeline expects { text, contact.email }', then: 'Mismatch — without an adapter the pipeline breaks.' },
        { do: 'Add rules: message → text, email → contact.email', then: 'The adapter now knows how to translate.' },
        { do: 'Point the form\'s webhook at your adapter URL', then: 'Each submission comes out as { text, contact.email } — ready for your pipeline.' },
      ],
    },
  },

  intake: {
    name: 'Multi-Specialty Intake',
    tagline: 'A smart receptionist that routes each inbound question to the right specialty desk — and escalates emergencies to a human.',
    how: [
      { t: 'You configure specialty desks', d: 'Auto-detected from your site (e.g. Cancer Screening, Billing…), or set your own. Each desk has its own knowledge.' },
      { t: 'A reception agent classifies each message', d: 'It reads the caller\'s message and decides which desk it belongs to, with a confidence score and an urgency level.' },
      { t: 'Emergencies escalate immediately', d: 'Distress or urgent language ("emergency", "domestic violence", "I\'m scared") is caught first and handed to a human — never left to a bot.' },
      { t: 'Otherwise it routes & captures', d: 'It sends the caller to the right desk, pulls that desk\'s knowledge, checks for conflicts, and records the intake.' },
    ],
    example: {
      scenario: 'A law firm (the original use case) with 6 practice areas fielding new callers.',
      steps: [
        { do: 'Caller: "I want to file for divorce and get custody"', then: 'Routed to the Family Law desk.' },
        { do: 'Caller: "My husband is threatening me, I\'m scared for my life"', then: 'Escalated to a human immediately — not routed to a bot.' },
        { do: 'On your reDNA account, the same engine detects health desks', then: '"Family history of breast cancer, what screening?" → Cancer Screening desk.' },
      ],
    },
  },

  agent: {
    name: 'Tool-using Agent / MCP',
    tagline: 'An AI agent that not only answers from your knowledge, but can DO things via tools — drivable from Claude, Cursor, the CLI, or your code.',
    how: [
      { t: 'The agent has a tool belt', d: 'Search your knowledge base, get the current time, send an email (once you connect a mailbox), and more.' },
      { t: 'You give it a task', d: 'In plain language. The agent decides which tools it needs.' },
      { t: 'It calls tools, then answers', d: 'It runs the tools (e.g. searches your KB), reads the results, and composes a final answer — showing which tools it used.' },
      { t: 'It speaks MCP', d: 'So AI coding tools like Claude and Cursor can use your agent as a live tool inside their own workflows.' },
    ],
    example: {
      scenario: 'You want the agent to look something up in your docs and act on it.',
      steps: [
        { do: 'Ask: "Find our refund window and email it to jane@acme.com"', then: 'The agent plans two tool calls.' },
        { do: 'It calls search_knowledge_base', then: 'Finds "Refunds within 30 days" in your content.' },
        { do: 'It calls send_email (once a mailbox is connected)', then: 'Emails Jane the answer, and tells you what it did.' },
      ],
    },
  },
}

// Ready-to-run sample inputs for the one-click "Try the example" button in the
// Services drawer. `query` runs immediately; `doc` pre-fills the RAG add-document box.
export const PRODUCT_SAMPLES = {
  answers: { query: 'What does the Advanced package include and what does it cost?' },
  sync: { query: 'family screening package price' },
  rag: {
    query: 'What screening packages do you offer and what do they cost?',
    doc: 'Refunds are accepted within 30 days of delivery for unworn items in the original packaging. Sale items are final and cannot be returned.',
  },
  orchestration: { query: 'I want to book a screening for my whole family' },
  middleware: {},
  intake: { query: 'I have a strong family history of breast cancer — what screening should I get?' },
  agent: { query: 'Search our knowledge base: what does the Advanced package cost?' },
}

export function guideFor(product) {
  return PRODUCT_GUIDES[product] || null
}
export function sampleFor(product) {
  return PRODUCT_SAMPLES[product] || {}
}
